
import React from 'react'

import { getPersonLabel } from 'utils'

import type { Customer } from 'types'

import { Stack, Typography, Skeleton } from '@mui/material'

import { Avatar } from '@repo/ui'
import { Preview } from 'components'

interface OwnerPreviewProps {
  owner: Customer | null | undefined
  onChange?: any,
  onRemove?: any,
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

export const OwnerPreview = (props: OwnerPreviewProps) => {
  const { owner, onChange = false, onRemove = false, disabled = false, size = 'large'} = props
  if (!owner) return null
  // `const theme = useTheme()` was here, BELOW the early return — a hooks-order
  // violation. It was also unused: the only `theme` reference in this file is the
  // `sx={theme => ...}` callback below, which shadows it. Deleted rather than hoisted.
  // VERA-304: an owner with no name shows the EMAIL on the primary line and no
  // second line — never a placeholder name over the address. See `getPersonLabel`.
  // The row does not change height: at `medium`/`large` the 48/64px avatar is
  // taller than either one or two lines of text, and `alignItems="center"` keeps
  // it on the same axis, so a named row and a nameless one sit flush in one list.
  const { primary, secondary } = getPersonLabel(owner)

  return (
    <Preview size={size} item={owner} onRemove={onRemove} onChange={onChange} disabled={disabled}>
      <Stack direction="row" alignItems="center" spacing={size == 'small' ? 2 : 4} >
        <Avatar src={owner?.avatar as string} name={primary as string} sx={{
          width: size == 'large' ? 64 : size == 'medium' ? 48 : 28,
          height: size == 'large' ? 64 : size == 'medium' ? 48 : 28
        }} />
        <Stack spacing={2} sx={{ minWidth: 0, maxWidth: size == 'small' ? 100 : '100%' }}>
          {/* `body2` whether it holds a name or an address: the primary line keeps
              the primary treatment, so an email-only row reads as a person rather
              than as a lone caption. */}
          <Typography variant="body2" noWrap>
            {primary}
          </Typography>
          {size != 'small' && secondary && (
            <Typography variant="body3" color="text.secondary" noWrap>
              {secondary}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Preview>
  )
}

export const OwnerPreviewLoader = () => {
  return (
    <Stack p="12px" sx={theme => ({ border: `1px solid ${theme.palette.grey[300]}` })}>
      <Stack spacing="16px" direction="row" alignItems="center">
        <Stack>
          <Skeleton variant="circular" width={64} />
        </Stack>
        <Stack spacing={2} width={'100%'}>
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={'100%'} />
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={'100%'} />
        </Stack>
      </Stack>
    </Stack>
  )
}